import type { User } from "../types";
import { create } from "zustand";
import axios from "axios";
import { API_BASE_URL } from "../utils/apiBaseUrl";
import { isTokenExpired } from "../utils/jwtUtils";

interface RefreshResponse {
  token: string;
  refreshToken: string;
  user: User;
}

interface AuthStore {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, refreshToken: string, user: User) => void;
  logout: () => void;
  setUser: (user: User | null) => void;
  updateTokens: (token: string, refreshToken: string) => void;
  /**
   * Tente de rafraîchir l'authentification à partir du refresh token.
   * Retourne le nouveau access token si succès, sinon null (et logout).
   */
  refreshAuth: () => Promise<string | null>;
  /**
   * Vérifie l'état d'authentification au démarrage:
   * - token OK: authentifié
   * - token expiré: refresh
   * - token manquant + refresh présent: refresh
   */
  checkAuth: () => Promise<void>;
}

const safeLocalStorage = {
  getItem(key: string): string | null {
    try {
      if (typeof localStorage?.getItem !== "function") return null;
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem(key: string, value: string): void {
    try {
      if (typeof localStorage?.setItem !== "function") return;
      localStorage.setItem(key, value);
    } catch {
      // En environnements contraints (tests, iframe, privacy mode), localStorage peut échouer.
    }
  },
  removeItem(key: string): void {
    try {
      if (typeof localStorage?.removeItem !== "function") return;
      localStorage.removeItem(key);
    } catch {
      // idem
    }
  },
};

const lireUserDepuisStorage = (): User | null => {
  const raw = safeLocalStorage.getItem("user");
  if (!raw) return null;
  try {
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: lireUserDepuisStorage(),
  token: safeLocalStorage.getItem("token"),
  refreshToken: safeLocalStorage.getItem("refreshToken"),
  isAuthenticated: !!safeLocalStorage.getItem("token"),
  isLoading: false,

  login: (token: string, refreshToken: string, user: User) => {
    safeLocalStorage.setItem("token", token);
    safeLocalStorage.setItem("refreshToken", refreshToken);
    safeLocalStorage.setItem("user", JSON.stringify(user));
    set({ user, token, refreshToken, isAuthenticated: true, isLoading: false });
  },

  logout: () => {
    safeLocalStorage.removeItem("token");
    safeLocalStorage.removeItem("refreshToken");
    safeLocalStorage.removeItem("user");
    set({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },

  setUser: (user: User | null) => {
    if (user) {
      safeLocalStorage.setItem("user", JSON.stringify(user));
    } else {
      safeLocalStorage.removeItem("user");
    }
    set({ user });
  },

  updateTokens: (token: string, refreshToken: string) => {
    safeLocalStorage.setItem("token", token);
    safeLocalStorage.setItem("refreshToken", refreshToken);
    set({ token, refreshToken });
  },

  refreshAuth: async () => {
    const { refreshToken } = get();
    if (!refreshToken) {
      safeLocalStorage.removeItem("token");
      safeLocalStorage.removeItem("refreshToken");
      safeLocalStorage.removeItem("user");
      set({
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
      });
      return null;
    }

    try {
      const response = await axios.post<RefreshResponse>(
        `${API_BASE_URL}/auth/refresh`,
        { refreshToken },
        { headers: { "Content-Type": "application/json" } },
      );

      const { token, refreshToken: newRefreshToken, user } = response.data;
      safeLocalStorage.setItem("token", token);
      safeLocalStorage.setItem("refreshToken", newRefreshToken);
      safeLocalStorage.setItem("user", JSON.stringify(user));

      set({
        token,
        refreshToken: newRefreshToken,
        user,
        isAuthenticated: true,
      });

      return token;
    } catch {
      safeLocalStorage.removeItem("token");
      safeLocalStorage.removeItem("refreshToken");
      safeLocalStorage.removeItem("user");
      set({
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
      });
      return null;
    }
  },

  checkAuth: async () => {
    if (get().isLoading) return;
    set({ isLoading: true });

    const { token, refreshToken } = get();

    // Aucun token: si on a un refresh token, tenter un refresh. Sinon: non authentifié.
    if (!token) {
      if (refreshToken) {
        await get().refreshAuth();
      } else {
        set({ isAuthenticated: false, user: null });
      }
      set({ isLoading: false });
      return;
    }

    // Token présent mais expiré: tenter un refresh.
    if (isTokenExpired(token)) {
      await get().refreshAuth();
      set({ isLoading: false });
      return;
    }

    // Token présent et non-expiré: on considère l'utilisateur authentifié.
    set({ isAuthenticated: true, isLoading: false });
  },
}));
